import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, fmtEUR } from "@/components/StatusBadge";
import { Layers, ArrowRight, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function ChantierDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [chantier, setChantier] = useState(null);
  const [lots, setLots] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/chantiers/${id}`);
        setChantier(data.chantier);
        setLots(data.lots || []);
      } catch (e) {
        toast.error(readError(e));
      }
    })();
  }, [id]);

  if (!chantier) return <div className="p-10 mono text-sm">Chargement…</div>;

  const isOwner = user?.id === chantier.demandeur_id;
  const total = lots.reduce((s, l) => s + (l.accepted_amount || 0), 0);
  const completed = lots.filter((l) => l.status === "completed").length;
  const inEscrow = lots.filter((l) => l.status === "paid").length;

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00]" data-testid="back-dashboard">
          ← Dashboard
        </Link>

        <div className="mt-4 border-2 border-[#0A0A0B] bg-white p-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] flex items-center gap-2">
                <Layers className="w-3.5 h-3.5" /> Chantier multi-intervenants
              </div>
              <h1 className="display text-4xl mt-2">{chantier.title}</h1>
              <div className="text-sm text-zinc-600 mt-2">
                {chantier.city}
                {chantier.deadline && ` · livraison ${chantier.deadline}`}
              </div>
            </div>
          </div>
          <p className="mt-6 text-zinc-700 whitespace-pre-line">{chantier.description}</p>

          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#0A0A0B]">
            <Info label="Lots" value={lots.length} />
            <Info label="En séquestre" value={inEscrow} />
            <Info label="Terminés" value={completed} />
            <Info label="Budget cumulé" value={fmtEUR(total)} />
          </div>
        </div>

        <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
          <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
            <div className="display text-2xl">Lots du chantier</div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">{lots.length}</div>
          </div>

          {lots.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">Aucun lot pour ce chantier.</div>
          )}

          {lots.map((m, idx) => (
            <Link
              key={m.id}
              to={`/missions/${m.id}`}
              className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition"
              data-testid={`chantier-lot-${m.id}`}
            >
              <div className="col-span-12 lg:col-span-1">
                <div className="display text-2xl text-[#FF4D00]">{String(idx + 1).padStart(2, "0")}</div>
              </div>
              <div className="col-span-12 lg:col-span-5">
                <div className="display text-xl">{m.title}</div>
                <div className="text-xs text-zinc-500 mt-1">{m.category}</div>
              </div>
              <div className="col-span-6 lg:col-span-2 flex items-center mono text-sm font-semibold">
                {m.accepted_amount
                  ? fmtEUR(m.accepted_amount)
                  : `${fmtEUR(m.budget_min)}—${fmtEUR(m.budget_max)}`}
              </div>
              <div className="col-span-6 lg:col-span-3 flex items-center">
                <StatusBadge status={m.status} />
              </div>
              <div className="col-span-12 lg:col-span-1 flex items-center justify-end">
                <ArrowRight className="w-4 h-4 text-zinc-400" />
              </div>
            </Link>
          ))}

          {isOwner && (
            <div className="border-t-2 border-[#0A0A0B] p-4 text-center">
              <Link to={`/missions/new?chantier_id=${chantier.id}`}>
                <button className="border-2 border-[#0A0A0B] px-4 py-2 hover:bg-[#0A0A0B] hover:text-white mono text-[11px] uppercase tracking-widest flex items-center gap-2 mx-auto" data-testid="add-lot-to-chantier">
                  <Plus className="w-3.5 h-3.5" /> Ajouter un lot à ce chantier
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="p-4 border-r-2 last:border-r-0 border-[#0A0A0B]">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="display text-lg mt-1">{value}</div>
    </div>
  );
}
